import React, { useState } from "react";

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7h16M9 7V4.5h6V7M6.5 7l.8 12.5h9.4L17.5 7M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20l4.2-1 10-10a2 2 0 0 0-2.8-2.8l-10 10L4 20zM14.5 7.5l2 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function formatWhen(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
}

export default function ConversationList({
  conversations,
  loading,
  error,
  hasMore,
  activeId,
  onOpen,
  onNew,
  onDelete,
  onDeleteAll,
  onRename,
  onRetry,
  onLoadMore,
}) {
  // Inline confirmations instead of window.confirm: nothing is deleted on a
  // single accidental tap.
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setActionError("");
    try {
      await fn();
    } catch (err) {
      setActionError(err?.message || "Не вдалося виконати дію");
    } finally {
      setBusy(false);
    }
  };

  const commitRename = (id) =>
    run(async () => {
      const title = draft.trim();
      setEditingId(null);
      if (title) await onRename(id, title);
    });

  return (
    <aside className="cvai-list" aria-label="Розмови з Cvanto AI">
      <div className="cvai-list__head">
        <h2 className="cvai-list__title">Розмови</h2>
        <button type="button" className="cvai-list__new" onClick={onNew}>
          <PlusIcon />
          <span>Нова</span>
        </button>
      </div>

      {error && (
        <div className="cvai-list__error" role="alert">
          <span>{error}</span>
          <button type="button" className="cvai-link-btn" onClick={onRetry}>
            Повторити
          </button>
        </div>
      )}
      {actionError && (
        <div className="cvai-list__error" role="alert">
          {actionError}
        </div>
      )}

      <div className="cvai-list__items">
        {loading && conversations.length === 0 && !error &&
          Array.from({ length: 5 }).map((_, i) => <div className="cvai-list__skeleton" key={`sk-${i}`} />)}

        {!loading && !error && conversations.length === 0 && (
          <p className="cvai-list__empty">Тут зʼявляться ваші розмови з Cvanto AI.</p>
        )}

        {conversations.map((c) => {
          const active = c.id === activeId;
          const confirming = confirmId === c.id;
          const editing = editingId === c.id;

          return (
            <div key={c.id} className={`cvai-list__item ${active ? "is-active" : ""}`}>
              {editing ? (
                <input
                  className="cvai-list__rename"
                  value={draft}
                  autoFocus
                  maxLength={80}
                  aria-label="Нова назва розмови"
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitRename(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                    if (e.key === "Escape") setEditingId(null);
                  }}
                />
              ) : (
                <button type="button" className="cvai-list__open" onClick={() => onOpen(c.id)} aria-current={active ? "true" : undefined}>
                  <span className="cvai-list__item-title">{c.title}</span>
                  <span className="cvai-list__item-meta">
                    {c.lastMessagePreview ? `${c.lastMessagePreview} · ` : ""}
                    {formatWhen(c.lastMessageAt)}
                  </span>
                </button>
              )}

              {!editing &&
                (confirming ? (
                  <span className="cvai-list__confirm">
                    <button type="button" className="cvai-list__danger" disabled={busy} onClick={() => run(async () => { await onDelete(c.id); setConfirmId(null); })}>
                      Видалити
                    </button>
                    <button type="button" className="cvai-link-btn" onClick={() => setConfirmId(null)}>
                      Скасувати
                    </button>
                  </span>
                ) : (
                  <span className="cvai-list__tools">
                    <button type="button" aria-label="Перейменувати розмову" onClick={() => { setDraft(c.title); setEditingId(c.id); }}>
                      <PencilIcon />
                    </button>
                    <button type="button" aria-label="Видалити розмову" onClick={() => setConfirmId(c.id)}>
                      <TrashIcon />
                    </button>
                  </span>
                ))}
            </div>
          );
        })}

        {hasMore && (
          <button type="button" className="cvai-link-btn cvai-list__more" onClick={onLoadMore} disabled={loading}>
            Показати ще
          </button>
        )}
      </div>

      {conversations.length > 0 && (
        <div className="cvai-list__foot">
          {confirmAll ? (
            <span className="cvai-list__confirm">
              <span className="cvai-list__foot-text">Видалити всі розмови?</span>
              <button type="button" className="cvai-list__danger" disabled={busy} onClick={() => run(async () => { await onDeleteAll(); setConfirmAll(false); })}>
                Так, видалити
              </button>
              <button type="button" className="cvai-link-btn" onClick={() => setConfirmAll(false)}>
                Скасувати
              </button>
            </span>
          ) : (
            <button type="button" className="cvai-link-btn" onClick={() => setConfirmAll(true)}>
              Видалити всі розмови
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
